<template>
  <Teleport to="body">
    <Transition name="notification">
      <div v-if="isVisible" class="notification-overlay" @click.self="handleClose">
        <div class="notification-container" :class="type">
          <div class="notification-icon">
            <svg v-if="type === 'success'" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
            <svg v-else-if="type === 'error'" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="15" y1="9" x2="9" y2="15"></line>
              <line x1="9" y1="9" x2="15" y2="15"></line>
            </svg>
            <svg v-else-if="type === 'warning'" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
              <line x1="12" y1="9" x2="12" y2="13"></line>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
            <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="16" x2="12" y2="12"></line>
              <line x1="12" y1="8" x2="12.01" y2="8"></line>
            </svg>
          </div>
          <div class="notification-content">
            <h3 v-if="title" class="notification-title">{{ title }}</h3>
            <p class="notification-message">{{ message }}</p>
          </div>
          <div class="notification-actions">
            <button @click="handleClose" class="notification-button" :class="type">
              OK
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useNotificationStore } from '@/stores/notificationStore';

const store = useNotificationStore();

const isVisible = computed(() => store.isVisible);
const message = computed(() => store.message);
const title = computed(() => store.title);
const type = computed(() => store.type);

function handleClose() {
  store.hide();
}
</script>

<style scoped>
.notification-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10001;
  padding: 1rem;
}

.notification-container {
  background: #ffffff;
  border-radius: 12px;
  box-shadow:
    0 25px 50px -12px rgba(0, 0, 0, 0.25),
    0 0 0 1px rgba(15, 23, 42, 0.05);
  width: 100%;
  max-width: 400px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.notification-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.notification-container.success .notification-icon {
  background-color: #ecfdf5;
  color: #059669;
}

.notification-container.error .notification-icon {
  background-color: #fef2f2;
  color: #dc2626;
}

.notification-container.warning .notification-icon {
  background-color: #fffbeb;
  color: #d97706;
}

.notification-container.info .notification-icon {
  background-color: #eff6ff;
  color: #2563eb;
}

.notification-content {
  text-align: center;
}

.notification-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 0.5rem 0;
}

.notification-message {
  font-size: 0.9375rem;
  color: #4b5563;
  margin: 0;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

.notification-actions {
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: 0.5rem;
}

.notification-button {
  padding: 0.625rem 2rem;
  border-radius: 0.5rem;
  font-size: 0.9375rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.notification-button.success {
  background-color: #059669;
  color: white;
}

.notification-button.success:hover {
  background-color: #047857;
}

.notification-button.error {
  background-color: #dc2626;
  color: white;
}

.notification-button.error:hover {
  background-color: #b91c1c;
}

.notification-button.warning {
  background-color: #d97706;
  color: white;
}

.notification-button.warning:hover {
  background-color: #b45309;
}

.notification-button.info {
  background-color: #2563eb;
  color: white;
}

.notification-button.info:hover {
  background-color: #1d4ed8;
}

/* Transitions */
.notification-enter-active,
.notification-leave-active {
  transition: opacity 0.2s ease;
}

.notification-enter-from,
.notification-leave-to {
  opacity: 0;
}

.notification-enter-active .notification-container,
.notification-leave-active .notification-container {
  transition: transform 0.2s ease;
}

.notification-enter-from .notification-container {
  transform: scale(0.95) translateY(-10px);
}

.notification-leave-to .notification-container {
  transform: scale(0.95) translateY(-10px);
}
</style>
